import { simulateReadableStream, type LanguageModelV1, type LanguageModelV1CallOptions, type LanguageModelV1StreamPart } from "ai"
import { loadApiKeys } from "../agents/integrations/channels/cache"
import { createModel } from "../agents/utils"

const DELAY = 25
const keys = new Map<string, Promise<APIKeys | null>>()

// The team's keys are read once per team and reused by every model after it
export async function loadModel(team: string, name: string): Promise<LanguageModelV1> {
  if (!keys.has(team)) keys.set(team, loadApiKeys(team))
  const apiKeys = await keys.get(team)
  if (!apiKeys) {
    keys.delete(team)
    throw new Error("Could not load the team's api keys")
  }
  const model = createModel(apiKeys, name)
  if (!model) throw new Error(`"${name}" is not a valid model`)
  return model
}

// What a fake does in one step. The script is called once per step, with the prompt so far
export interface FakeStep {
  thinking?: string
  text?: string
  calls?: { name: string, args: any }[]
}

export type FakeScript = (prompt: LanguageModelV1CallOptions["prompt"]) => FakeStep

function words(text?: string) {
  return text ? text.match(/\S+\s*/g) ?? [] : []
}

// A model that streams what the script says word by word, so a fake turn flows through the same loop as a real one
export function fakeModel(script: FakeScript): LanguageModelV1 {
  let counter = 0
  return {
    specificationVersion: "v1",
    provider: "fake",
    modelId: "fake",
    defaultObjectGenerationMode: undefined,
    doGenerate: () => { throw new Error("The fake model only streams") },
    doStream: async options => {
      const step = script(options.prompt)
      const chunks: LanguageModelV1StreamPart[] = [
        ...words(step.thinking).map(textDelta => ({ type: "reasoning" as const, textDelta })),
        ...words(step.text).map(textDelta => ({ type: "text-delta" as const, textDelta })),
        ...(step.calls ?? []).map(c => ({ type: "tool-call" as const, toolCallType: "function" as const, toolCallId: `fake_${counter++}`, toolName: c.name, args: JSON.stringify(c.args) })),
        { type: "finish", finishReason: step.calls?.length ? "tool-calls" : "stop", usage: { promptTokens: 0, completionTokens: 0 } },
      ]
      return { stream: simulateReadableStream({ chunks, chunkDelayInMs: DELAY }), rawCall: { rawPrompt: null, rawSettings: {} } }
    },
  }
}
